import { SetMirrorData } from '@type/job-add';
import {
  Label, ComboBox, Input,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { useTranslation } from 'next-i18next';

/** Props */
interface JobCareerLevelsProps {
  apiCareerLevels?: ApiSchema<'OptionItem'>[],
  record: RecordInterface,
  formValues: object,
  setFormValues: SetFormValues,
  initialSelectedValues?: string[],
  checkboxName?: string,
  labelDescriptionClass?: string,
  setMirrorData?: SetMirrorData,
}

/**
 * @description Job Career Levels component. To be used in a form. The text input field is omitted from submit.
 * - `apiCareerLevels` - static list of career levels from the API
 * - `record` - record function from useBjForm
 * - `formValues` - form values object from useBjForm
 * - `setFormValues` - setFormValues function from useBjForm
 * - `initialSelectedValues` - initial selected values for combobox (ie: useBjForm.withDefaultValues.careerLevels)
 * - `checkboxName` - name of the checkbox; default is 'careerLevels'
 * - `labelDescriptionClass` - additional classes for the label description
 * - `setMirrorData` - function to update the mirror form state
 */
export const JobCareerLevels = (props: JobCareerLevelsProps) => {
  // Destructure props
  const {
    apiCareerLevels,
    record,
    formValues,
    setFormValues,
    initialSelectedValues,
    checkboxName = 'careerLevels',
    labelDescriptionClass = '',
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Render
  // **************************
  return (
    <Label
      required
      as="div"
      description={t('job.add.label.career')}
      descriptionClass={labelDescriptionClass}
    >
      <ComboBox
        required
        className="max-w-full text-ellipsis pr-18"
        options={{
          staticList: apiCareerLevels,
          valueFromKey: 'name',
          translateFn: t,
          unmount: false,
          keepOpen: true,
          behaveAs: 'select',
          selectOptions: {
            multiple: true,
            checkboxName,
            checkboxValueFromKey: 'id',
            setFormValues,
            initialSelectedValues,
            updatedSelectedValues: formValues?.[checkboxName as keyof typeof formValues],
          },
        }}
        onBlur={() => setMirrorData?.()}
        onClear={() => {
          setFormValues({ [checkboxName]: [] });
          setMirrorData?.();
        }}
        {...record('careerLevelsSelector', {
          removeFromSubmit: true,
        })}
        renderList={(
          item,
          index,
          listKey,
          getItemProps,
          listStyling,
        ) => {
          const careerItem = item as ApiSchema<'OptionItem'>;
          return (
            <li key={listKey} {...getItemProps({ item, index })} className="px-3">
              <span className={`${listStyling} cursor-pointer items-center text-sm`}>
                <Input
                  type="checkbox"
                  {...record(checkboxName, {
                    id: `${checkboxName}${careerItem.id}`,
                    defaultValue: careerItem.id,
                    valueAsArray: true,
                  })}
                />
                <span className="pl-2.5">
                  {careerItem.name}
                </span>
              </span>
            </li>
          );
        }}
      />
    </Label>
  );
};
